<template>
  <WrappedForm
    ref="form"
    class="question-editor"
    label-position="top"
    label-bold
    hide-required-asterisk
    :model="questionData"
    :formLines="formLines"
  >
    <template #options="{lineName, line }">
      <div class="options-tip">{{ line.tipMessage }}</div>
      <div class="options-container">
        <div class="options">
          <label v-if="showKnockoutOptions">{{ line.optionsLabel }}</label>
          <el-input
            v-model="questionData[lineName]"
            ref="optionsInput"
            class="options-input"
            type="textarea"
            :autosize="{ minRows: 5}"
            :name="lineName"
            :placeholder="line.placeholder"
            @blur="onOptionsInputBlur"
          />
        </div>
        <div v-if="showKnockoutOptions" class="options knockout">
          <label>{{ $t(`${i18nPrefix}.label.markAsKnockoutOptions`) }}</label>
          <el-select
            v-model="questionData.knockoutOptions"
            multiple
            class="knockout-options-select"
            name="knockoutOptions"
            :placeholder="$t(`${i18nPrefix}.placeholder.markAsKnockoutOptions`)"
          >
            <el-option v-for="(item) in questionOptionList" :key="item._vid" :label="item.label" :value="item._vid"></el-option>
          </el-select>
        </div>
      </div>
    </template>
    <template #groupSelect="{ lineName, line }">
      <el-select
        v-model="questionData[lineName]"
        :name="lineName"
        :disabled="line.disabled"
        :placeholder="line.placeholder"
      >
        <el-option
          v-for="(option, index) in line.option.data"
          :key="index"
          :label="option.i18n_name"
          :value="option.id"
        />
      </el-select>
    </template>
  </WrappedForm>
</template>

<script>
import _ from 'lodash'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import { QUESTION_TYPE, HAS_OPTIONS_QUESTION_TYPES, VIDEO_DURATION, VISIBILITY_TYPE, HAS_KNOCKOUT_OPTIONS_QUESTION_TYPES } from '@/constant/openQuestion'
import { createI18nProperty } from '@/i18n'
import { i18nPrefix } from './common'

export const visibilityOptionList = _.map(VISIBILITY_TYPE, (value, typeName) => {
  const option = { value, typeName }
  createI18nProperty(option, 'label', `${i18nPrefix}.visibilityType.${typeName}`)
  return option
})

export const questionTypeOptionList = _.map(QUESTION_TYPE, (value, typeName) => {
  const option = { value, typeName }
  createI18nProperty(option, 'label', `${i18nPrefix}.questionType.${typeName}`)
  return option
})

export default {
  components: {
    WrappedForm
  },
  props: {
    isTemplate: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      required: true
    },
    groupList: {
      type: Array,
      default: null
    },
    question: {
      required: false
    },
    disabledQuestionType: {
      type: Array,
      default: () => []
    },
    hasVisibilityOption: {
      type: Boolean,
      default: false
    },
    defaultVisibility: {
      type: String,
      default: VISIBILITY_TYPE.IE
    },
    showUpdateButton: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix,
      QUESTION_TYPE,
      questionData: {
        id: null,
        groupId: -1,
        type: QUESTION_TYPE.TEXT,
        title: null,
        hasKnockout: false,
        options: null,
        knockoutOptions: null,
        videoDuration: null,
        updateButton: false
      },
      questionOptionList: []
    }
  },
  computed: {
    form () {
      return this.$refs.form
    },
    questionWatcher () {
      return this.question
    },
    questionTypeList () {
      return _.filter(questionTypeOptionList, ({ value }) => !this.disabledQuestionType.includes(value))
    },
    defaultQuestionType () {
      return this.questionTypeList[0].value
    },
    formLines () {
      const vm = this
      const questionData = this.questionData
      const requiredMessage = this.$t('common.message.requiredFormField')
      const lines = {
        groupId: {
          elType: 'el-select',
          contentSlot: 'groupSelect',
          hidden: !this.isTemplate,
          label: this.$t(`${this.i18nPrefix}.label.group`),
          option: {
            data: this.groupList,
            labelKey: 'i18n_name',
            valueKey: 'id'
          }
        },
        type: {
          elType: 'el-select',
          disabled: this.isEdit || this.questionTypeList.length === 1,
          label: this.$t(`${this.i18nPrefix}.label.questionType`),
          option: {
            data: this.questionTypeList
          }
        },
        visibility: {
          elType: 'el-select',
          hidden: !this.hasVisibilityOption,
          label: this.$t(`${this.i18nPrefix}.label.visibility`),
          option: {
            data: visibilityOptionList
          },
          rules: [
            { required: true, message: requiredMessage, trigger: 'blur' }
          ]
        },
        title: {
          label: this.$t(`${this.i18nPrefix}.label.questionTitle`),
          rules: [
            { required: true, message: requiredMessage, trigger: 'blur' }
          ],
          props: {
            type: 'textarea',
            autosize: {
              minRows: 4
            }
          }
        },
        hasKnockout: {
          elType: 'el-checkbox',
          class: 'knockout-checkbox-line',
          get hidden () {
            return !HAS_KNOCKOUT_OPTIONS_QUESTION_TYPES.includes(questionData.type)
          },
          checkboxLabel: this.$t(`${this.i18nPrefix}.label.includeKnockoutOptions`)
        },
        options: {
          get hidden () {
            return !HAS_OPTIONS_QUESTION_TYPES.includes(questionData.type)
          },
          label: this.$t(`${this.i18nPrefix}.label.options`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.options`),
          contentSlot: 'options',
          optionsLabel: this.$t(`${this.i18nPrefix}.label.allOptions`),
          get tipMessage () {
            let tip = vm.$t(`${vm.i18nPrefix}.message.notKnockoutOptionsTip`)
            if (questionData.hasKnockout) {
              tip += ' ' + vm.$t(`${vm.i18nPrefix}.message.knockoutOptionsTip`)
            }
            return tip
          },
          rules: [
            { required: true, message: requiredMessage, trigger: 'blur' }
          ]
        },
        videoDuration: {
          elType: 'el-radio-group',
          get hidden () {
            return questionData.type !== QUESTION_TYPE.VIDEO
          },
          label: this.$t(`${this.i18nPrefix}.label.duration`),
          option: {
            data: _.map(VIDEO_DURATION, (value) => {
              return { label: value, value }
            })
          }
        },
        updateButton: {
          elType: 'el-checkbox',
          get hidden () {
            return !vm.isEdit || !vm.showUpdateButton || !HAS_OPTIONS_QUESTION_TYPES.includes(questionData.type)
          },
          checkboxLabel: this.$t(`${this.i18nPrefix}.label.createUpdateTask`)
        }
      }
      return lines
    },
    showKnockoutOptions () {
      const questionData = this.questionData
      return HAS_KNOCKOUT_OPTIONS_QUESTION_TYPES.includes(questionData.type) && questionData.hasKnockout
    }
  },
  created () {

  },
  methods: {
    parse (questionData) {
      if (!questionData) {
        questionData = this.questionData
      }
      const options = []
      const knockoutOptions = questionData.knockoutOptions || []
      _.forEach(this.questionOptionList, (option, index) => {
        const vid = option._vid
        options.push({
          description: option.value,
          is_knockout: knockoutOptions.includes(vid) ? 1 : 0,
          order: index + 1
        })
      })
      const question = {
        id: questionData.id,
        group_id: questionData.groupId,
        type: questionData.type,
        title: questionData.title,
        options,
        duration: questionData.videoDuration,
        need_update: questionData.updateButton || false
      }
      if (this.hasVisibilityOption) {
        question.visibility = questionData.visibility
      }
      if (questionData.type !== QUESTION_TYPE.VIDEO) {
        question.duration = null
      }
      if (!HAS_OPTIONS_QUESTION_TYPES.includes(questionData.type)) {
        question.options = null
      }
      return question
    },
    toData (question) {
      if (question && Object.keys(question).length > 0) {
        let hasKnockout = false
        const options = []
        const knockoutOptions = []
        const questionOptionList = []
        _.forEach(question.options, (item, index) => {
          const option = item.description
          const vid = `${index}_${option}`
          if (item.is_knockout === 1) {
            hasKnockout = true
            knockoutOptions.push(vid)
          }
          questionOptionList.push({
            _vid: vid,
            index: index,
            label: option,
            value: option
          })
          options.push(option)
        })
        this.questionOptionList = questionOptionList
        return {
          id: question.id,
          groupId: question.group_id,
          type: question.type || this.defaultQuestionType,
          title: question.title,
          hasKnockout,
          options: options.join('\n'),
          knockoutOptions: knockoutOptions,
          videoDuration: question.duration || VIDEO_DURATION.S30,
          visibility: this.hasVisibilityOption ? question.visibility || this.defaultVisibility : undefined,
          updateButton: false
        }
      } else {
        this.questionOptionList = []
        return {
          id: null,
          groupId: -1,
          type: this.defaultQuestionType,
          title: null,
          hasKnockout: false,
          options: null,
          knockoutOptions: [],
          videoDuration: VIDEO_DURATION.S30,
          visibility: this.hasVisibilityOption ? VISIBILITY_TYPE.IE : undefined,
          updateButton: false
        }
      }
    },
    clearData () {
      this.questionData = this.toData(null)
      this.$nextTick(() => this.clearValidate())
    },
    clearValidate () {
      this.form.clearValidate()
    },
    async submit () {
      const isValid = await this.form.submit()
      if (!isValid) {
        return null
      } else {
        return this.parse()
      }
    },
    onOptionsInputBlur () {
      const questionData = this.questionData
      const newVIds = []
      const allOptions = _.filter((questionData.options || '').split('\n')).map((item, index) => {
        const vid = `${index}_${item}`
        newVIds.push(vid)
        return {
          _vid: vid,
          index: index,
          label: item,
          value: item
        }
      })
      const knockoutOptions = questionData.knockoutOptions
      if (knockoutOptions && knockoutOptions.length > 0) {
        questionData.knockoutOptions = _.filter(knockoutOptions, item => newVIds.includes(item))
      }
      this.questionOptionList = allOptions
    }
  },
  watch: {
    questionWatcher: {
      handler () {
        this.questionData = this.toData(this.question)
        this.$nextTick(() => this.clearValidate())
      },
      immediate: true
    },
    'questionData.type' () {
      this.$nextTick(() => this.clearValidate())
    }
  }
}
</script>

<style lang="scss" scoped>
.question-editor {
  ::v-deep .knockout-checkbox-line {
    margin-top: 32px;
  }

  .options-tip {
    line-height: initial;
    white-space: break-spaces;
    word-break: keep-all;
  }

  .options-container {
    display: flex;

    .options {
      flex: 1;
      display: flex;
      flex-direction: column;

      &.knockout {
        margin-left: 20px;
      }

      .knockout-options-select {
        flex: 1;
        border: 1px solid #DCDFE6;
        cursor: pointer;

        ::v-deep {
          .el-select__tags {
            top: 0;
            transform: none;
          }

          .el-input {
            height: 100%;

            .el-input__inner {
              border: 0
            }

            .el-input__suffix {
              display: none;
            }
          }
        }
      }
    }
  }
}

.el-select-dropdown__item {
  min-width: 100%;
  max-width: 40vw;
  overflow: hidden;
  word-break: keep-all;
  text-overflow: ellipsis;
}
</style>
